<template>
  <div class="job-actions-modal">
    <h5>{{ $t('jobs.share-modal.share') }} {{ job.title }}</h5>
      <share class="w-100"
        :url="absoluteShareURL"
        @track="({ channel }) => trackJobSharedEvent({ channel })"
        :providers="canShareJobsWith"
        :preview-text="jobShareText" :preview-args="{ jobTitle: job.title }"
        source="job"
        @openLinkedInJobConnectionModal="$bvModal.show(`connect-jobs-linkedin-${job.id}`)"
        @openLinkedInJobsModal="$bvModal.show(`job-share-linkedin-post-editor-${job.id}`)"/>

      <hr class="my-3"/>

      <h5>{{ $t('jobs.share-modal.share') }} {{ job.title }}</h5>
      <advanced-form v-bind="referForm" @submit="form => createReferral(form)">
        <template v-slot:formControl-after>
          <b-form-text class="mt-3"><a class="text-small" href="#" v-b-toggle.learn-more>{{ $t('jobs.share-modal.whatHappens') }}</a></b-form-text>
          <b-collapse id="learn-more">
            <hr/>
            <p>{{ $t('jobs.share-modal.whatHappensAnswer') }}</p>
          </b-collapse>
        </template>
      </advanced-form>
  </div>
</template>

<script>
import AdvancedForm from '@src/components/AdvancedForm';
import ReferralsCreateOneMutation from '@src/graphql/mutations/ReferralsCreateOne.gql';
import Share from '@/components/Share';
import { mapRoot, mapPreferences } from '@src/mixins/root';
import ConnectLinkedIn from '@/components/content-share/ConnectLinkedIn';

export default {
  name: 'JobActionsModal',
  props: {
    modalID: {
      type: String,
    },
    job: {
      type: Object,
      required: true,
      validate: entry => entry && entry.id && entry.title,
    },
    event: {
      type: Object,
      default: null,
      validate: entry => entry && entry.id && entry.title,
    },
  },
  components: {
    AdvancedForm,
    Share,
    ConnectLinkedIn,
  },
  computed: {
    ...mapRoot([ 'origin', 'isEXPD8', 'isAssurant', 'isHelloFresh' ]),
    ...mapRoot({
      canShareJobsWith({ company }) {
        const { preferences, sitePreferences, sharePreferences } = company || {};
        const { canShareJobsWith } = { ...preferences, ...sitePreferences, ...sharePreferences };
        return canShareJobsWith;
      },
      jobShareText({ company }) {
        const { preferences, sitePreferences, ...sharePreferences } = company || {};
        const { preferences: eventPreferences } = this.event || {};
        const { customFieldsHash: customFields } = this.job || {};
        const { jobShareText } = { ...preferences, ...sitePreferences, ...sharePreferences, ...eventPreferences, ...customFields };

        if (this.isAssurant && this.user && this.user.locale === 'fr') {
          return 'Assurant recrute ! Nous recherchons un(e) {jobTitle} pour rejoindre notre équipe. Consultez l’annonce et postulez maintenant !';
        }

        if (this.isHelloFresh && this.user && this.user.locale === 'de') {
          return 'Hast du Lust mit mir bei HelloFresh als {jobTitle} zu arbeiten? Jetzt bewerben!';
        }

        return jobShareText || 'Are you interested in working with me as a {jobTitle}?';
      },
    }),
    ...mapPreferences([ 'featuresEnabled' ]),
    absoluteShareURL() {
      const { origin } = this;
      const { id: eventID } = this.event || {};
      const { shareCode } = this.job || {};

      if (origin && shareCode) {
        let prefix = null;

        if (eventID) {
          prefix = `/e/r/${eventID}/`;
        } else {
          prefix = '/r/';
        }

        return `${this.origin}${prefix}${shareCode}`;
      } else {
        return null;
      }
    },
    referForm() {
      const form = {
        fullName: {
          label: this.$t('jobs.share-modal.labels.contactName'),
          placeholder: this.$t('jobs.share-modal.placeholders.contactName'),
          required: true,
          type: 'STRING',
        },
        emailAddress: {
          label: this.$t('jobs.share-modal.labels.emailAddress'),
          placeholder: this.$t('jobs.share-modal.placeholders.emailAddress'),
          required: true,
          type: 'STRING',
          options: {
            autocomplete: 'off',
          },
          validation: {
            isEmailAddress: true,
          },
        },
      };

      return {
        autocomplete: 'off',
        layout: 'inline-md-3',
        submitLabel: this.$t('jobs.share-modal.share'),
        form,
      };
    },
  },
  methods: {
    async trackJobSharedEvent({ channel }) {
      try {
        const { id: jobID, campaign } = this.job;
        const { id: eventID } = this.event || {};
        const { id: campaignID } = campaign || {};
        await this.$root.captureLog({
          eventName: 'JOB_SHARED',
          event: eventID ? { id: eventID } : undefined,
          campaign: campaignID ? { id: campaignID } : undefined,
          job: { id: jobID },
          user: { self: true },
          dataHash: { channel },
        });
      } catch (err) {
        this.$log.error(err);
      }
      this.$emit('onCompleted');
    },
    async createReferral(form) {
      try {
        const { id: jobID } = this.job;
        const { id: eventID } = this.event || {};
        this.$assert(jobID, 'Missing jobID for referral');

        const { fullName, emailAddress } = form;
        this.$assert(typeof fullName === 'string' && fullName.length, 'Missing fullName for referral');
        this.$assert(typeof emailAddress === 'string' && emailAddress.length, 'Missing emailAddress for referral');
        this.$assert(emailAddress.includes('@'), 'Please enter a valid email address');

        const create = {
          job: { id: jobID },
          user: { self: true },
          event: eventID ? { id: eventID } : undefined,
          fullName,
          emailAddress,
          status: 'SUGGESTED',
        };

        this.$log.debug({ create });

        await this.$apollo.mutate({
          mutation: ReferralsCreateOneMutation,
          variables: { create },
        });

        this.$emit('onCompleted');
        // eslint-disable-next-line no-unused-expressions
        this.modalID ? this.$bvModal.hide(this.modalID) : undefined;
      } catch (err) {
        this.$log.error(err);
        this.$notifyErr(err);
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/modals/job-actions-modal.vue